import { useEffect, useState } from "react";
import { dataExplorerAPI } from "../../../services/dataExplorerAPI";
import {
  DataExplorerPlotConfig,
  DataExplorerPlotResponse,
  ErrorTypeError,
  LinRegInfo,
} from "@depmap/types";

export default function usePlotData(plotConfig: DataExplorerPlotConfig | null) {
  const [data, setData] = useState<DataExplorerPlotResponse | null>(null);
  const [linreg_by_group, setLinRegInfoByGroup] = useState<LinRegInfo[] | null>(
    null
  );
  const [
    // HACK: The distinction between `plotConfig` and `fetchedPlotConfig`
    // exists so stale content can be shown while new content is being fetched.
    // However, this is an admittedly confusing way to achieve that. React 18
    // has a pattern for this called `useDeferredValue`.
    fetchedPlotConfig,
    setFetchedPlotConfig,
  ] = useState<DataExplorerPlotConfig | null>(null);
  const [hadError, setHadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    (async () => {
      if (!plotConfig) {
        setData(null);
        setFetchedPlotConfig(null);
        setHadError(false);
        return;
      }

      if (fetchedPlotConfig === plotConfig) {
        return;
      }

      if (
        fetchedPlotConfig &&
        fetchedPlotConfig.plot_type !== plotConfig.plot_type
      ) {
        // Clear out the data whenever the plot type is changed. We do this
        // because we try to keep the plot rendered in the background (even
        // while loading new data). However, that makes no sense to do with
        // incompatible plots.
        setData(null);
      }

      try {
        setHadError(false);
        const fetchedData = await dataExplorerAPI.fetchPlotData(plotConfig);

        setData(fetchedData);
        setFetchedPlotConfig(plotConfig);
      } catch (e) {
        setHadError(true);
        window.console.error(e);

        // Distinguish Error from ErrorTypeError
        if (e instanceof ErrorTypeError) {
          if (e.errorType === "LARGE_DATASET_READ") {
            // we happen to want to keep the default message but here we name it explicitly for visibility and maybe change it in the future
            setErrorMessage(e.message);
          } else {
            setErrorMessage(e.message);
          }
        } else if (e instanceof Error) {
          setErrorMessage(e.message as string);
        } else {
          setErrorMessage("");
        }
      }
    })();
  }, [plotConfig, fetchedPlotConfig]);

  useEffect(() => {
    setLinRegInfoByGroup(null);

    (async () => {
      if (plotConfig) {
        try {
          const fetchedData = await dataExplorerAPI.fetchPlotLinearRegression(
            plotConfig
          );

          // Double check that these match (the user could've tweaked the config
          // while this request was still in flight).
          if (plotConfig === fetchedPlotConfig) {
            setLinRegInfoByGroup(fetchedData);
          }
        } catch (e) {
          window.console.error(e);
        }
      }
    })();
  }, [plotConfig, fetchedPlotConfig]);

  // The stale-content-while-fetching behavior above only works when the old
  // response is shape-compatible with the incoming config. Toggling an
  // expansion on or off breaks that: a config with facet_by/color_by
  // "expansion" rendered against a stale non-expanded response throws in
  // findCategoricalSlice (mode "expansion" requires data.expansions). That
  // throw happens during the very render triggered by the dispatch — before
  // any effect could clear the data — so the mask must be computed here at
  // render time, not via setData(null) in the fetch effect.
  const isStaleDataCompatible =
    !fetchedPlotConfig ||
    !plotConfig ||
    "expand_by" in fetchedPlotConfig === "expand_by" in plotConfig;

  return {
    data: isStaleDataCompatible ? data : null,
    linreg_by_group,
    fetchedPlotConfig,
    hadError,
    errorMessage,
  };
}
